// Стандартные стилизованные диалоговые окна.
import AlertDialog from "./AlertDialog.vue";
import ConfirmDialog from "./ConfirmDialog.vue";
import PromptDialog from "./PromptDialog.vue";
import GeneralDialog from "./GeneralDialog.vue";
import {i18n} from "../../index.js";

/**
 * Плагин для объединения функций для открытия простых диалоговых окон,
 * а также настраиваемых с предустановками для использования поверх vuedals.
 *
 * По сути это шорткаты для vuedals,
 * поэтому необходимо выполнить стандартную процедуру установки самого vuedals как плагина, так и компонента.
 *
 * Плагин регистрирует во Vue свойство `$camsdals`, через которое организуется доступ к функциям открытия диалогов.
 * Для стандартных (браузерных) диалогов используются собственные компоненты диалогов.
 */
export default {
  install(Vue) {
    // Стандартные параметры для передачи в функцию открытия диалога vuedals.
    const defaultsForVuedals = Object.freeze({
      name: "",
      closeOnBackdrop: false,
      dismissable: false,
      escapable: true,
      size: "sm",
    });

    Vue.prototype.$camsdals = new Vue({
      name: "Camsdals",
      methods: {
        /**
         * Откроет диалог, в который передается Vue компонент с версткой и готовым функционалом.
         * Внешне компонент будет обернут в заготовку со стилями диалогового окна
         *
         * @param {Object} componentWithContent Vue компонент с телом для показа в диалоге
         * @param {Object} propsForDialog Свойства для переданного компонента с контентом
         * @param {Object} propsForContent Свойства для диалогового компонента {@link GeneralDialog}, в котором будет контент
         * @param {Object} customsForVuedals Параметры для настройки Vuedals
         */
        open(componentWithContent, propsForContent, propsForDialog = {}, customsForVuedals = {}) {
          this.$vuedals.open({
            ...defaultsForVuedals,
            ...customsForVuedals,
            component: GeneralDialog,
            props: {
              componentWithContent,
              propsForContent,
              ...propsForDialog,
            },
          });
        },
        /**
         * Диалог для показа простого текстового сообщения (например ошибок).
         *
         * @param {Object|String} propsForContent Свойства для компонента {@link AlertDialog} или только текст сообщения
         * @param {Object} propsForDialog Свойства для диалогового компонента {@link GeneralDialog}
         * @param {Object} customsForVuedals Параметры для настройки Vuedals
         */
        alert(propsForContent = {messageText: i18n.t('alertMessage')}, propsForDialog = {dialogTitle: i18n.t('alertDialogTitle')}, customsForVuedals = {}) {
          this.open(
            AlertDialog,
            _.isString(propsForContent) ? {messageText: propsForContent} : propsForContent,
            propsForDialog,
            customsForVuedals
          );
        },
        /**
         * Диалог для подтверждения операции.
         *
         * @param {Object|String} propsForContent Свойства для компонента {@link ConfirmDialog} или только текст сообщения
         * @param {Function} fnConfirmed Функция, которая сработает при подтверждении операции
         * @param {Object} propsForDialog Свойства для диалогового компонента {@link GeneralDialog}
         * @param {Object} customsForVuedals Параметры для настройки Vuedals
         */
        confirm(propsForContent, fnConfirmed, propsForDialog = {dialogTitle: i18n.t('confirmDialogTitle')}, customsForVuedals = {}) {
          this.open(
            ConfirmDialog,
            _.isString(propsForContent) ? {messageText: propsForContent} : propsForContent,
            propsForDialog,
            {
              onClose: (isConfirmed) => (isConfirmed ? fnConfirmed() : null),
              ...customsForVuedals,
            }
          );
        },
        /**
         * Диалог с полем ввода и возможностью вернуть введенный текст при закрытии диалога.
         *
         * @param {Object|String} propsForContent Свойства для компонента {@link PromptDialog} или только текст сообщения
         * @param {Function} fnPrompted Функция, которая сработает при отправке введенного текста и в качестве аргумента примет этот текст
         * @param {Object} propsForDialog Свойства для диалогового компонента {@link GeneralDialog}
         * @param {Object} customsForVuedals Параметры для настройки Vuedals
         */
        prompt(propsForContent, fnPrompted, propsForDialog = {dialogTitle: i18n.t('promptDialogTitle')}, customsForVuedals = {}) {
          this.open(
            PromptDialog,
            _.isString(propsForContent) ? {messageText: propsForContent} : propsForContent,
            propsForDialog,
            {
              onClose: (promptedText) => (promptedText ? fnPrompted(promptedText) : null),
              ...customsForVuedals,
            }
          );
        },
      },
    });
  },
};
